import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ModalService } from '@iota/iota-ui-components';
import {
    DialogShowDocumentComponent,
    DialogShowDocumentData
} from 'src/app/shared/dialogs/dialog-show-document/dialog-show-document.component';
import {
    AttributesValueType,
    CaseStatusType,
    EmployeeCaseAttributeDTO,
    EmployeeCaseControllerService,
    EmployeeCaseDTO
} from '@iota/iota-management-portal-v4-client';
import {
    ManagementSubmissionDTO,
    RegisteredEmployeeDetailView,
    ReimbursementAuthorizationManagementControllerService,
    RequestCategoryLevel1,
    SubmissionManagementControllerService
} from '@iota/inh-core-v1-management-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, take, toArray } from 'rxjs/operators';
import { CaseAttribute } from 'src/app/shared/constants-and-enums/attribute-types';
import { getEmployeeCaseLink } from 'src/app/shared/constants-and-enums/routing';
import { SUBMISSION_ERROR_MESSAGES } from 'src/app/shared/constants-and-enums/submission-labels';
import { DefaultEmployeeCase } from 'src/app/shared/constants-and-enums/default-objects';
import { getLocationHeader, isNotNullOrUndefined, isValidNumber, isValidString } from 'src/app/shared/utils';
import { submissionErrorKind } from 'src/app/shared/utils/submission-error.util';
import {
    AddNoteToCase,
    AddNoteToCaseSuccess,
    CaseSubmissionsRejected,
    CreateCaseAndOpen,
    CreateCaseSuccess,
    EmployeeCaseActionTypes,
    GenerateInhCertificate,
    GenerateInhCertificateFailure,
    GenerateInhCertificateSuccess,
    LoadEmployeeCaseById,
    LoadEmployeeCasesFailure,
    RemoveEmployeeCaseAttachment,
    RemoveEmployeeCaseAttachmentFailure,
    RemoveEmployeeCaseAttachmentSuccess,
    RemoveNoteFromCase,
    RemoveNoteFromCaseSuccess,
    UpdateCase,
    UpdateCaseStatus,
    UpdateCaseStatusFailure,
    UpdateCaseStatusSuccess,
    UpdateCaseSuccess,
    UpsertCaseAttribute,
    UpsertCaseAttributeSuccess,
    UpsertEmployeeCaseAttachment,
    UpsertEmployeeCaseAttachmentSuccess,
    RejectRequestsByCase
} from '../actions/employee-case.actions';
import { DeleteTransactionSuccess, UpsertTransactionSuccess } from '../actions/employee-transaction.actions';
import { employeeCaseByEmployeeCaseId } from '../reducer/employee-case.reducer';
import { ToastService } from 'src/app/shared/services/toast.service';
import { buildEmployeeRequestEntities } from '../entity-builder-utils';
import { EmployeeRequestEntity } from '../entity-models';

@Injectable()
export class EmployeeCaseEffects {
    reloadEmployeeCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                AddNoteToCaseSuccess,
                RemoveNoteFromCaseSuccess,
                UpdateCaseSuccess,
                UpdateCaseStatusSuccess,
                UpsertCaseAttributeSuccess,
                UpsertTransactionSuccess,
                DeleteTransactionSuccess,
                RejectRequestsByCase,
                LoadEmployeeCaseById,
                UpsertEmployeeCaseAttachmentSuccess,
                RemoveEmployeeCaseAttachmentSuccess
            ),
            mergeMap(({ caseId }) =>
                this.apiService.getEmployeeCase(caseId).pipe(
                    map((employeeCase) => ({
                        type: EmployeeCaseActionTypes.LoadEmployeeCasesSuccess,
                        cases: [employeeCase]
                    })),
                    catchError((error) => of({ type: EmployeeCaseActionTypes.LoadEmployeeCasesFailure, error: error }))
                )
            )
        )
    );

    addNoteToCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AddNoteToCase),
            mergeMap(({ caseId, note }) =>
                this.apiService.upsertEmployeeCaseNote(caseId, { note }).pipe(
                    map(() => ({
                        type: EmployeeCaseActionTypes.AddNoteToCaseSuccess,
                        caseId: caseId
                    })),
                    catchError((error) => of({ type: EmployeeCaseActionTypes.AddNoteToCaseFailure, error: error }))
                )
            )
        )
    );

    removeNoteFromCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveNoteFromCase),
            mergeMap(({ caseId, noteId }) =>
                this.apiService.removeEmployeeCaseNote(noteId).pipe(
                    map(() => ({
                        type: EmployeeCaseActionTypes.RemoveNoteFromCaseSuccess,
                        caseId: caseId
                    })),
                    catchError((error) => of({ type: EmployeeCaseActionTypes.RemoveNoteFromCaseFailure, error: error }))
                )
            )
        )
    );

    createCaseAndOpen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateCaseAndOpen),
            mergeMap(
                ({
                    employee,
                    amount,
                    dateOfClaimStart,
                    dateOfClaimEnd,
                    diagnosis,
                    exportNote,
                    gkvReimbursement,

                    request,
                    subscriptionIds,
                    subSubscriptionIds
                }) =>
                    this.createEmployeeCase(
                        amount,
                        dateOfClaimStart,
                        dateOfClaimEnd,
                        diagnosis,
                        exportNote,
                        gkvReimbursement,
                        employee,
                        request,
                        subscriptionIds,
                        subSubscriptionIds
                    ).pipe(
                        map((response) => {
                            const employeeId = employee.registeredEmployeeId;
                            const caseId = getLocationHeader(response);
                            if (caseId) {
                                this.router.navigate([getEmployeeCaseLink(employeeId, caseId)], { queryParamsHandling: 'preserve' });
                            }
                            return CreateCaseSuccess({ employeeId, caseId });
                        }),
                        catchError((error) => of(LoadEmployeeCasesFailure({ error })))
                    )
            )
        )
    );

    updateCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateCase),
            mergeMap(({ employeeCase }) =>
                this.apiService
                    .upsertEmployeeCase(employeeCase, 'response')
                    .pipe(take(1))
                    .pipe(
                        map((response) => ({
                            type: EmployeeCaseActionTypes.UpdateCaseSuccess,
                            caseId: getLocationHeader(response)
                        })),
                        catchError((error) => of({ type: EmployeeCaseActionTypes.UpdateCaseFailure, error: error }))
                    )
            )
        )
    );

    updateCaseStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateCaseStatus),
            concatLatestFrom(({ caseId }) => this.store.select(employeeCaseByEmployeeCaseId(caseId))),
            mergeMap(([{ caseId, newStatus, reason }, employeeCase]) =>
                this.apiService.updateEmployeeCaseStatus({ employeeCaseId: caseId, newStatus }).pipe(
                    take(1),
                    map(() => {
                        if (newStatus === CaseStatusType.DENIED) {
                            return RejectRequestsByCase({
                                employeeId: employeeCase.employeeId,
                                caseId,
                                requestIds: employeeCase.employeeRequestIds,
                                reason
                            });
                        }

                        return UpdateCaseStatusSuccess({ caseId });
                    }),
                    catchError((error) => of(UpdateCaseStatusFailure({ error })))
                )
            )
        )
    );

    rejectRequestsByCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RejectRequestsByCase),
            mergeMap(({ employeeId, caseId, requestIds, reason }) =>
                this.submissionService.getSubmissions(employeeId).pipe(
                    concatMap((submissions) => this.rejectOpenSubmissions(employeeId, openSubmissionIds(submissions, requestIds), reason)),
                    catchError((error: unknown) => {
                        this.toastService.showError(SUBMISSION_ERROR_MESSAGES[submissionErrorKind(error)]);
                        return of(undefined);
                    }),
                    map(() => CaseSubmissionsRejected({ employeeId, caseId }))
                )
            )
        )
    );

    upsertSelectedCaseAttribute$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCaseAttribute),
            mergeMap(({ caseId, attribute }) =>
                this.apiService.upsertEmployeeCaseAttributes(caseId, [attribute]).pipe(
                    map(() => ({ type: EmployeeCaseActionTypes.UpsertCaseAttributeSuccess, caseId: caseId })),
                    catchError((error) => of({ type: EmployeeCaseActionTypes.UpsertCaseAttributeFailure, error: error }))
                )
            )
        )
    );

    generateInhCertificate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GenerateInhCertificate),
            mergeMap(({ caseId }) =>
                this.reimbursementAuthorizationApi.generate(caseId, undefined, 'response').pipe(
                    map((response: HttpResponse<unknown>) => {
                        const blob = response.body as Blob;
                        const fileName = this.parseFilenameFromContentDisposition(response.headers.get('Content-Disposition'));
                        const data: DialogShowDocumentData = {
                            blob,
                            fileName: fileName ?? 'reimbursement-authorization.pdf',
                            contentType: 'application/pdf'
                        };
                        this.dialog.open(DialogShowDocumentComponent, { data });
                        return GenerateInhCertificateSuccess();
                    }),
                    catchError((error) => of(GenerateInhCertificateFailure({ error })))
                )
            )
        )
    );

    upsertEmployeeCaseAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertEmployeeCaseAttachment),
            mergeMap(({ file, employeeId, caseId }) =>
                this.apiService.upsertEmployeeCaseAttachment(caseId, employeeId, file).pipe(
                    map(() => ({ type: EmployeeCaseActionTypes.UpsertEmployeeCaseAttachmentSuccess, caseId: caseId })),
                    catchError((error) => of({ type: EmployeeCaseActionTypes.UpsertEmployeeCaseAttachmentFailure, error: error }))
                )
            )
        )
    );

    removeEmployeeCaseAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveEmployeeCaseAttachment),
            mergeMap(({ caseId, documentDescriptionId }) =>
                this.apiService.removeEmployeeCaseAttachment(documentDescriptionId).pipe(
                    map(() => RemoveEmployeeCaseAttachmentSuccess({ caseId })),
                    catchError((error) => {
                        this.toastService.showError(
                            $localize`Sorry, there was a technical issue. The attachment could not be deleted. If the problem persists please contact customer support.`
                        );
                        return of(RemoveEmployeeCaseAttachmentFailure({ error }));
                    })
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private apiService: EmployeeCaseControllerService,
        private reimbursementAuthorizationApi: ReimbursementAuthorizationManagementControllerService,
        private dialog: ModalService,
        private router: Router,
        private toastService: ToastService,
        private submissionService: SubmissionManagementControllerService
    ) {}

    private rejectOpenSubmissions(employeeId: string, submissionIds: string[], reason: string): Observable<void> {
        return from(submissionIds).pipe(
            concatMap((submissionId) =>
                this.submissionService.reject(employeeId, submissionId, { reason }).pipe(
                    map(() => ({ failed: false, error: undefined as unknown })),
                    catchError((error: unknown) => of({ failed: true, error }))
                )
            ),
            toArray(),
            map((outcomes) => {
                const firstFailure = outcomes.find((outcome) => outcome.failed);
                if (firstFailure) {
                    this.toastService.showError(SUBMISSION_ERROR_MESSAGES[submissionErrorKind(firstFailure.error)]);
                }
            })
        );
    }

    private parseFilenameFromContentDisposition(header: string | null): string | undefined {
        if (!header) return undefined;
        const match = /filename="([^"]+)"/.exec(header);
        return match?.[1];
    }

    private createEmployeeCase(
        amount: number,
        dateOfClaimStart: string | null,
        dateOfClaimEnd: string | null,
        diagnosis: string | null,
        exportNote: string | null,
        gkvReimbursement: number,
        employee: RegisteredEmployeeDetailView,
        selectedRequest: EmployeeRequestEntity,
        selectedSubscriptionIds: string[],
        selectedSubSubscriptionIds: string[]
    ): Observable<HttpResponse<any>> {
        const newCase: EmployeeCaseDTO = DefaultEmployeeCase();
        newCase.attributes = [];

        this.addNumericAttribute(newCase, CaseAttribute.AMOUNT, amount);
        this.addStringAttribute(newCase, CaseAttribute.AMOUNT_CURRENCY, 'EUR');

        if (selectedRequest?.categoryLevel1) {
            this.addStringAttribute(newCase, CaseAttribute.CATEGORY, selectedRequest.categoryLevel1);
        }

        const isPreventionCourseOrPreventionCoursePlusCase =
            selectedRequest?.categoryLevel1 === RequestCategoryLevel1.PREVENTIONCOURSES ||
            selectedRequest?.categoryLevel1 === RequestCategoryLevel1.PREVENTIONCOURSES_PLUS;

        if (isPreventionCourseOrPreventionCoursePlusCase) {
            this.addNumericAttribute(newCase, 'GKV_REIMBURSEMENT', gkvReimbursement);
        } else {
            this.addStringAttribute(newCase, 'DIAGNOSIS', diagnosis ?? '');
            this.addStringAttribute(newCase, 'EXPORT_NOTE', exportNote ?? '');
        }

        this.addEmployeeIds(newCase, employee);
        this.addSubscriptionIds(newCase, selectedSubscriptionIds, selectedSubSubscriptionIds);
        this.addRequestId(newCase, selectedRequest);

        this.addDates(newCase, dateOfClaimStart, dateOfClaimEnd);

        return this.apiService.upsertEmployeeCase(newCase, 'response').pipe(take(1));
    }

    private addNumericAttribute(newCase: EmployeeCaseDTO, name: string, value: number): void {
        if (isValidNumber(value)) {
            const attribute: EmployeeCaseAttributeDTO = {
                attributeName: name,
                numValue: [value],
                valueType: AttributesValueType.NUMBER
            };
            newCase.attributes.push(attribute);
        }
    }

    private addStringAttribute(newCase: EmployeeCaseDTO, name: string, value: string | null): void {
        if (isValidString(value)) {
            const attribute: EmployeeCaseAttributeDTO = {
                attributeName: name,
                strValue: [value.trim()],
                valueType: AttributesValueType.STRING
            };
            newCase.attributes.push(attribute);
        }
    }

    private addEmployeeIds(newCase: EmployeeCaseDTO, employee: RegisteredEmployeeDetailView): void {
        if (isNotNullOrUndefined(employee?.registeredEmployeeId)) newCase.employeeId = employee.registeredEmployeeId;
        if (isNotNullOrUndefined(employee?.companyId)) newCase.companyId = employee.companyId;
        if (isNotNullOrUndefined(employee?.office?.id)) newCase.officeId = employee.office.id;
    }

    private addSubscriptionIds(newCase: EmployeeCaseDTO, selectedSubscriptionIds: string[], selectedSubSubscriptionIds: string[]): void {
        if (Array.isArray(selectedSubscriptionIds) && selectedSubscriptionIds.length > 0) {
            newCase.employeeProductSubscriptionIds = selectedSubscriptionIds;
        }
        if (Array.isArray(selectedSubSubscriptionIds)) {
            newCase.employeeSubProductSubscriptionIds = selectedSubSubscriptionIds.length > 0 ? selectedSubSubscriptionIds : [];
        } else {
            newCase.employeeSubProductSubscriptionIds = [];
        }
    }

    private addRequestId(newCase: EmployeeCaseDTO, selectedRequest: EmployeeRequestEntity): void {
        if (isNotNullOrUndefined(selectedRequest?.id)) {
            newCase.employeeRequestIds = [selectedRequest.id];
        }
    }

    private addDates(newCase: EmployeeCaseDTO, dateOfClaimStart: string | null, dateOfClaimEnd: string | null): void {
        if (isNotNullOrUndefined(dateOfClaimStart)) {
            newCase.dateOfClaimStart = moment.utc(dateOfClaimStart).toDate();
        }
        if (isNotNullOrUndefined(dateOfClaimEnd)) {
            newCase.dateOfClaimEnd = moment.utc(dateOfClaimEnd).toDate();
        }
    }
}

function openSubmissionIds(submissions: ManagementSubmissionDTO[], caseSubmissionIds: string[]): string[] {
    return buildEmployeeRequestEntities(...submissions)
        .filter((submission) => caseSubmissionIds.includes(submission.id) && !submission.isClosed)
        .map((submission) => submission.id);
}
